import React from 'react';
import { QrCode, FileSpreadsheet, BarChart } from 'lucide-react';
import { colors, typography, spacing, components, layout, transitions, shadows } from '../styles/shared';

const styles = {
  hero: {
    backgroundColor: colors.background.page,
    paddingTop: `calc(${layout.header.height} + ${spacing[16]})`,
    paddingBottom: spacing[24],
    textAlign: 'center',
    position: 'relative',
    overflow: 'hidden',
    zIndex: 1,
  },
  container: {
    ...components.container,
    position: 'relative',
    zIndex: 1,
  },
  title: {
    ...typography.h1,
    color: colors.gray[900],
    marginBottom: spacing[6],
    maxWidth: '800px',
    margin: '0 auto',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '-20px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '60px',
      height: '4px',
      background: `linear-gradient(90deg, ${colors.primary[400]}, ${colors.primary[600]})`,
      borderRadius: '2px',
    },
  },
  highlight: {
    background: `linear-gradient(135deg, ${colors.primary[600]}, ${colors.primary[400]})`,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    display: 'inline-block',
  },
  subtitle: {
    ...typography.h4,
    color: colors.gray[600],
    maxWidth: '600px',
    margin: '0 auto',
    marginBottom: spacing[12],
    lineHeight: '1.6',
  },
  ctaButton: {
    ...components.button.base,
    ...components.button.primary,
    padding: `${spacing[4]} ${spacing[8]}`,
    fontSize: '1.125rem',
    gap: spacing[3],
    background: `linear-gradient(135deg, ${colors.primary[500]}, ${colors.primary[600]})`,
    boxShadow: `0 4px 6px ${colors.primary[400]}33`,
    marginBottom: spacing[12],
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 12px ${colors.primary[400]}66`,
      background: `linear-gradient(135deg, ${colors.primary[600]}, ${colors.primary[700]})`,
    },
  },
  ctaIcon: {
    transition: transitions.bounce,
    '.hero-button:hover &': {
      transform: 'translateX(4px)',
    },
  },
  features: {
    display: 'flex',
    justifyContent: 'center',
    gap: spacing[8],
    flexWrap: 'wrap',
    maxWidth: '900px',
    margin: '0 auto',
  },
  featureCard: {
    flex: '1 1 250px',
    maxWidth: '300px',
    padding: spacing[6],
    borderRadius: '16px',
    background: colors.background.card,
    border: `1px solid ${colors.gray[100]}`,
    transition: transitions.smooth,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: shadows.lg,
      borderColor: colors.primary[200],
    },
  },
  featureIcon: {
    width: '48px',
    height: '48px',
    color: colors.primary[500],
    marginBottom: spacing[4],
  },
  featureTitle: {
    ...typography.h4,
    color: colors.gray[900],
    marginBottom: spacing[2],
  },
  featureText: {
    ...typography.body2,
    color: colors.gray[600],
  },
  // Background decorations
  decoration: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '100%',
    background: `
      radial-gradient(circle at 20% 20%, ${colors.primary[50]}cc 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, ${colors.primary[50]}cc 0%, transparent 50%),
      radial-gradient(circle at 50% 50%, ${colors.primary[50]}66 0%, transparent 70%)
    `,
    opacity: 1,
    zIndex: 0,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '10%',
      left: '5%',
      width: '400px',
      height: '400px',
      background: `radial-gradient(circle, ${colors.primary[100]}66 0%, transparent 70%)`,
      borderRadius: '50%',
      animation: 'float 8s ease-in-out infinite',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: '10%',
      right: '5%',
      width: '500px',
      height: '500px',
      background: `radial-gradient(circle, ${colors.primary[100]}66 0%, transparent 70%)`,
      borderRadius: '50%',
      animation: 'float 12s ease-in-out infinite reverse',
    },
  },
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translate(0, 0)',
    },
    '50%': {
      transform: 'translate(20px, -20px)',
    },
  },
};

const Hero = () => {
  const handleScroll = () => {
    const generateSection = document.getElementById('generate-section');
    if (generateSection) {
      generateSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <section style={styles.hero}>
      <div style={styles.decoration} />
      <div style={styles.container}>
        <h1 style={styles.title}>
          Generate <span style={styles.highlight}>Bulk QR Codes</span> for Your Business Needs
        </h1>
        <p style={styles.subtitle}>
          Transform your CSV data into professional QR codes instantly. Perfect for business cards, 
          product labels, and marketing materials.
        </p>
        <button className="hero-button" style={styles.ctaButton} onClick={handleScroll}>
          Start Generating QR Codes
          <QrCode size={18} style={styles.buttonIcon} />
        </button>
        <div style={styles.features}>
          <div style={styles.featureCard}>
            <QrCode style={styles.featureIcon} />
            <h3 style={styles.featureTitle}>Bulk Generation</h3>
            <p style={styles.featureText}>
              Create hundreds of QR codes in seconds from your CSV data
            </p>
          </div>
          <div style={styles.featureCard}>
            <FileSpreadsheet style={styles.featureIcon} />
            <h3 style={styles.featureTitle}>CSV Import</h3>
            <p style={styles.featureText}>
              Easy data import from your spreadsheet files
            </p>
          </div>
          <div style={styles.featureCard}>
            <BarChart style={styles.featureIcon} />
            <h3 style={styles.featureTitle}>Multiple Formats</h3>
            <p style={styles.featureText}>
              Support for various QR code types and data formats
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero; 